import { setSyncReceiver } from "@repo/api";
import { createContext, type ReactNode, useContext, useEffect, useMemo } from "react";
import { applySync } from "./apply-sync";
import { DataStoreClient } from "./client";
import type { DataStoreClientConfig } from "./types";

const DataStoreContext = createContext<DataStoreClient | null>(null);

/** Module-level handle to the active client, set when DataStoreProvider mounts.
 * Lets non-React callers (intent actions, scripts) reach collections without context. */
let activeClient: DataStoreClient | null = null;

export function getDataStoreClient(): DataStoreClient {
  if (!activeClient) {
    throw new Error("DataStoreClient not initialized. Mount <DataStoreProvider> first.");
  }
  return activeClient;
}

export interface DataStoreProviderProps {
  children: ReactNode;
  /** Pre-configured client (optional - will create new if not provided) */
  client?: DataStoreClient;
  /** Configuration for creating a new client (ignored if client is provided) */
  config?: DataStoreClientConfig;
}

/**
 * Generic React provider for the data store.
 * Can accept a pre-configured client or create a new one.
 */
export function DataStoreProvider({ children, client: providedClient, config }: DataStoreProviderProps) {
  const client = useMemo(() => {
    return providedClient ?? new DataStoreClient(config);
  }, [providedClient, config]);

  useEffect(() => {
    activeClient = client;
    const clearSyncReceiver = setSyncReceiver((sync) => applySync(client, sync));
    client.preload();

    return () => {
      clearSyncReceiver();
      if (activeClient === client) activeClient = null;
      if (!providedClient) {
        client.dispose();
      }
    };
  }, [client, providedClient]);

  return <DataStoreContext.Provider value={client}>{children}</DataStoreContext.Provider>;
}

/**
 * Hook to access the DataStoreClient.
 * Must be used within a DataStoreProvider.
 */
export function useDataStoreClient(): DataStoreClient {
  const client = useContext(DataStoreContext);
  if (!client) {
    throw new Error("useDataStoreClient must be used within DataStoreProvider");
  }
  return client;
}
